import React from "react";
import { Star } from "lucide-react";
import DashboardPanel from "./DashboardPanel";
import {
  ALL_STATUSES,
  formatRate,
  getStatusColors,
} from "../../pages/performance/performanceUtils";

function StatusBadge({ status }) {
  const colors = getStatusColors(status);
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${colors.bg} ${colors.text} ${colors.border}`}
    >
      {status === "Top Performer" && <Star size={10} fill="currentColor" strokeWidth={0} />}
      {status || "Unrated"}
    </span>
  );
}

// One stacked bar showing how many assignees fall in each status.
function StatusDistribution({ byStatus, total }) {
  const extra = Object.keys(byStatus).filter((s) => !ALL_STATUSES.includes(s));
  const statuses = [...ALL_STATUSES, ...extra].filter((s) => byStatus[s] > 0);

  return (
    <div>
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700"
        role="img"
        aria-label={statuses.map((s) => `${s}: ${byStatus[s]}`).join(", ")}
      >
        {statuses.map((s) => (
          <div
            key={s}
            className="h-full"
            style={{ width: `${(byStatus[s] / total) * 100}%`, backgroundColor: getStatusColors(s).dot }}
            title={`${s}: ${byStatus[s]}`}
          />
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {statuses.map((s) => (
          <li key={s} className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1.5 text-app-secondary">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: getStatusColors(s).dot }}
              />
              <span className="truncate">{s}</span>
            </span>
            <span className="font-semibold tabular-nums text-app-primary">{byStatus[s]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TopList({ rows }) {
  return (
    <ol className="divide-y divide-gray-100 dark:divide-slate-700">
      {rows.map((row) => {
        const email = row.user?.email || "—";
        return (
          <li
            key={`${row.userId}-${row.project?.id ?? "all"}`}
            className="flex items-center gap-3 py-2.5"
          >
            <span className="w-6 shrink-0 text-sm font-semibold text-app-muted">#{row.rank}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-app-primary" title={email}>
                {email}
              </p>
              <p className="text-xs text-app-muted">
                {row.totalOutput} responses · {formatRate(row.conversionRate)} conversion
              </p>
            </div>
            <StatusBadge status={row.status} />
          </li>
        );
      })}
    </ol>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="h-3 w-full animate-pulse rounded-full bg-gray-200 dark:bg-slate-700" />
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="h-4 w-6 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
          <div className="h-4 flex-1 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
          <div className="h-4 w-20 animate-pulse rounded-full bg-gray-100 dark:bg-slate-700/60" />
        </div>
      ))}
    </div>
  );
}

/** Status spread across the team plus the top of the leaderboard. */
function PerformanceSnapshotCard({ data, loading = false, error = false }) {
  let body;
  if (loading) body = <Skeleton />;
  else if (error) body = <p className="text-sm text-app-error">{"Couldn't load performance data."}</p>;
  else if (!data?.total) body = <p className="text-sm text-app-muted">No performance data available.</p>;
  else
    body = (
      <div className="space-y-5">
        <StatusDistribution byStatus={data.byStatus} total={data.total} />
        <div>
          <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-app-muted">
            Top performers
          </h4>
          <TopList rows={data.top} />
        </div>
      </div>
    );

  return (
    <DashboardPanel
      title="Performance Snapshot"
      description="All projects, ranked by screener responses, then conversion rate."
      action={{ label: "Performance dashboard", to: "/admin/performance-dashboard" }}
    >
      {body}
    </DashboardPanel>
  );
}

export default PerformanceSnapshotCard;
